import { ChevronIcon, CloseIcon, IconButton, Scrollbar, Text } from "@carbon6/c6-frontend-design";
import { appTextModes } from "@/shared/ui/text";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useModalSurface } from "./useModalSurface";
import { FULLSCREEN_WINDOW_Z } from "./windowLayers";
import styles from "./FullscreenWindow.module.css";

/**
 * 스텝 헤더를 좌우로 훑는 이동. 지정하면 셸이 스텝 헤더 양옆에 [←] [→] 버튼을 그리고 `←`/`→`
 * 키를 함께 받는다 — 어느 스텝이 이웃인지는 셸이 모르므로 호출부가 두 동작만 내려준다.
 * 양 끝에서는 disabled 로 두어 "더 갈 곳이 없다"를 버튼이 직접 말한다(순환하지 않는다).
 */
export type FullscreenWindowStepNav = {
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  // 접근성 이름 겸 툴팁. 무엇으로 옮겨가는지는 호출부만 안다.
  prevLabel: string;
  nextLabel: string;
};

/**
 * 본문이 셸이 내준 남은 높이를 쓰는 방식. 미지정 시 `scroll`.
 * - `scroll`: 셸이 스크롤 컨테이너가 된다. 자식들은 행으로 쌓이고 간격은 셸의 gap 이 준다.
 *   본문 높이가 확정돼 있어 자식이 `height: 100%` 로 본문을 채우는 것도 여전히 된다.
 * - `fill`: 셸은 스크롤하지 않고 높이만 내준다. 스크롤 영역은 본문이 직접 소유한다
 *   (2단 레이아웃처럼 한쪽만 스크롤하거나, 자체 `Scrollbar` 를 쓰는 창).
 */
export type FullscreenWindowBodyLayout = "scroll" | "fill";

export type FullscreenWindowProps = {
  // 노출(controlled).
  open: boolean;
  // 헤더 좌측 제목(데이터셋명 등). accessible name(aria-label) 으로도 쓰인다.
  title: string;
  // 제목 오른쪽 배지 슬롯 — 이 창이 **무엇을 보고 있는지**에 붙는 상태(예: 데이터셋 파생 처리
  // 중)를 둔다. 우측 액션 슬롯과 나누는 기준은 조작 가능 여부다: 누를 수 있으면 headerActions,
  // 읽기만 하면 여기다. 제목은 이 배지가 차지한 만큼 더 일찍 말줄임된다. 선택.
  titleBadge?: ReactNode;
  // 제목 아래 스텝 헤더 슬롯(예: 1 Embodiment · 2 Labels · 3 Confirm). 선택.
  stepHeader?: ReactNode;
  // 스텝 헤더 양옆 이동 버튼 + `←`/`→` 키. 미지정이면 버튼도 키 바인딩도 없다. 이 축은 셸이
  // 소비하고 내용으로 내려보내지 않는다.
  stepNav?: FullscreenWindowStepNav;
  /**
   * `↑`/`↓` 를 내용이 받는다 — 무엇을 세로로 훑을지는 창마다 다르다(섹션 목록·에피소드 목록 등).
   * 미지정이면 셸이 그 키를 잡지 않으므로, 본문이 자체 리스너로 화살표를 쓰는 창과 안 다툰다.
   * 입력창·수식키·이미 소비된 키 양보는 셸이 소유하니 콜백은 이동만 하면 된다.
   */
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  // 우측 윈도우 컨트롤(최소화/닫기) 왼쪽에 두는 페이지 액션 슬롯(예: 북마크·archive). 선택.
  headerActions?: ReactNode;
  // [_] 최소화 — 전역 dock 으로 접는다. 없으면 버튼 자체를 그리지 않는다(작업표시줄에 자리가 없는
  // 중첩 창은 최소화하면 되돌릴 길이 사라진다).
  onMinimize?: () => void;
  // [X] 종료 요청. Escape 도 이 콜백으로 매핑. 호출부는 스택의 `requestClose` 로 넘긴다. 콘텐츠가 등록한
  // 닫기 guard 가 참이면 스택이 닫기 전에 확인을 받는다.
  onRequestClose: () => void;
  // 바깥(백드롭) 클릭 시 호출. 미지정이면 백드롭 클릭은 무시한다. 셸은 정책(닫기/최소화)을 모르고
  // 호출부가 결정한다 — 데이터셋 창은 최소화로 연결한다.
  onBackdrop?: () => void;
  // dialog 본문.
  children: ReactNode;
  // 본문 아래 고정 푸터 슬롯(예: 확정 버튼). 미지정이면 푸터 바 자체를 그리지 않는다.
  footer?: ReactNode;
  // accessible name 이 title 과 달라야 할 때(선택). 기본은 title.
  ariaLabel?: string;
  // 본문의 높이·스크롤 계약. 미지정 시 "scroll".
  bodyLayout?: FullscreenWindowBodyLayout;
  /** 이 너비 아래에서는 창 전체를 가로로 스크롤한다. 기본은 60rem(960px)이다. */
  minWidth?: CSSProperties["minWidth"];
  // 윈도우 컨트롤 버튼 라벨(접근성·툴팁). 기본 국문.
};

// 전체화면 창 셸. **c6 Modal 이 아니라** body portal 커스텀 오버레이다 — c6 Modal 은 중앙 dialog
// 셸(backdrop·모션·마크업)을 전제해 전체화면 창 프레임(헤더 그리드·푸터 바·최소화)을 표현하기에
// 맞지 않는다. 대신 c6 modalStack 에 직접 등록해 c6 Modal 과 같은 규칙으로 조율한다:
// - scroll lock·바깥 inert 는 스택이 소유한다(중첩 c6 Modal 과 ref-count/topmost 로 안 엉킨다).
// - dialog 를 popup mount node 로 등록해 창 안 AnchoredPopup 계열(드롭다운 메뉴 등)이 dialog 안으로
//   portal 된다 — body 로 떨어지면 popover z(20) < 오버레이 z(880)라 메뉴가 창 뒤에 가려진다.
// - 중첩 c6 Modal 은 body 레벨 형제(z 1000~)라 이 오버레이 위에 쌓이고, 떠 있는 동안 스택이 이
//   오버레이를 inert 처리한다.
export function FullscreenWindow({
  open,
  title,
  titleBadge,
  stepHeader,
  stepNav,
  onArrowUp,
  onArrowDown,
  headerActions,
  onMinimize,
  onRequestClose,
  onBackdrop,
  children,
  footer,
  ariaLabel,
  bodyLayout = "scroll",
  minWidth = "60rem",
}: FullscreenWindowProps): ReactNode {
  const zIndex = FULLSCREEN_WINDOW_Z;
  const { container, dialogRef, onBackdropClick, onBackdropMouseDown } = useModalSurface({
    active: open,
    rootAttributeName: "data-fullscreen-window-root",
    zIndex,
    onRequestClose,
    onBackdrop,
    // 좌/우는 셸이 소비하고, 위/아래는 내용의 이동 정책만 콜백으로 받는다.
    hotkeys: [
      { key: "ArrowLeft", onPress: stepNav?.prevDisabled === true ? undefined : stepNav?.onPrev },
      { key: "ArrowRight", onPress: stepNav?.nextDisabled === true ? undefined : stepNav?.onNext },
      { key: "ArrowUp", onPress: onArrowUp },
      { key: "ArrowDown", onPress: onArrowDown },
    ],
  });

  if (container === null) {
    return null;
  }

  return createPortal(
    <Scrollbar
      className={styles.overlay}
      orientation="horizontal"
      gutter="auto"
      style={{ zIndex }}
      hidden={!open}
      inert={!open}
      // scrim 은 표현용 — 바깥 클릭은 onBackdrop(최소화) 편의 동작이고, 키보드는 Escape(종료)로 커버된다.
      role="presentation"
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
    >
      <div
        className={styles.dialog}
        style={{ minWidth }}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        tabIndex={-1}
        ref={dialogRef}
      >
        {/* 3분할: 제목(좌) · 스텝 헤더(가운데 정렬) · 윈도우 컨트롤(우). 가운데 auto 컬럼은
            항상 렌더해 컨트롤이 우측 컬럼에 고정되게 한다(stepHeader 없어도 빈 셀 유지). */}
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <Text variant={appTextModes["body-bold"]} className={styles.title} title={title}>
              {title}
            </Text>
            {titleBadge}
          </div>
          {/* 스텝 헤더와 그 양옆 이동 버튼. 글리프는 셰브론 하나뿐이고 테두리도 버튼 하나만 갖는다
              — 키캡을 함께 두면 키캡 상자와 버튼이 겹쳐 테두리가 두 겹으로 보인다. 단축키는
              글리프 대신 툴팁이 말한다. 좌/우는 같은 셰브론을 반대로 눕혀 표현을 맞춘다. */}
          <div className={styles.stepSlot}>
            {stepNav ? (
              <IconButton
                aria-label={stepNav.prevLabel}
                title={`${stepNav.prevLabel} (←)`}
                type="button"
                // 우측 윈도우 컨트롤과 같은 테두리 — 양 끝 disabled 도 같은 규칙으로 읽힌다.
                variant="surface"
                size="sm"
                disabled={stepNav.prevDisabled}
                onClick={stepNav.onPrev}
              >
                <ChevronIcon className={styles.stepNavCaretPrev} />
              </IconButton>
            ) : null}
            {stepHeader}
            {stepNav ? (
              <IconButton
                aria-label={stepNav.nextLabel}
                title={`${stepNav.nextLabel} (→)`}
                type="button"
                variant="surface"
                size="sm"
                disabled={stepNav.nextDisabled}
                onClick={stepNav.onNext}
              >
                <ChevronIcon className={styles.stepNavCaretNext} />
              </IconButton>
            ) : null}
          </div>
          <div className={styles.headerRight}>
            {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
            <div className={styles.windowControls}>
              {onMinimize ? (
                <IconButton
                  aria-label="최소화"
                  title="창을 최소화합니다."
                  type="button"
                  variant="surface"
                  size="sm"
                  onClick={onMinimize}
                >
                  <MinimizeIcon />
                </IconButton>
              ) : null}
              <IconButton
                aria-label="종료"
                title="창을 종료합니다."
                type="button"
                variant="surface"
                size="sm"
                onClick={onRequestClose}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </header>
        <div
          className={[
            styles.body,
            bodyLayout === "fill" ? styles.bodyFill : styles.bodyScroll,
          ].join(" ")}
        >
          {children}
        </div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </Scrollbar>,
    container,
  );
}

function MinimizeIcon() {
  return (
    <svg aria-hidden="true" width="1em" height="1em" viewBox="0 0 16 16">
      <path
        d="M3.5 12h9"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}
